import { motion } from "framer-motion";
import { Globe, Users, Zap, TrendingUp, MapPin, Award, Heart, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import globalNetwork from "../assets/global-network.jpg";
import teamSolar from "../assets/team-solar.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";
import StatsGrid from "../components/StatsGrid";
import EnergyParticles from "../components/EnergyParticles";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const Community = () => (
  <div>
    <PageHero
      eyebrow="Global Community"
      title={<>A Worldwide <span className="energy-gradient-text">Renewable Network</span></>}
      description="32,000+ partners across 45 countries are united by the shared mission of powering the future with clean energy. Our community grows stronger every day."
      backgroundImage={globalNetwork}
    />

    <Section>
      <StatsGrid stats={[
        { value: "32,000+", label: "Active Partners" },
        { value: "45+", label: "Countries" },
        { value: "6", label: "Continents" },
        { value: "£18M+", label: "Total Distributed" },
      ]} />
    </Section>

    {/* World Map */}
    <Section className="bg-secondary/20 relative overflow-hidden">
      <EnergyParticles />
      <div className="relative z-10">
        <SectionHeading eyebrow="Global Reach" title="Connected Across the World" description="Our partner network spans every inhabited continent, with major communities in Europe, Asia, Middle East, and the Americas." />
        <motion.div {...fadeUp} className="relative rounded-2xl overflow-hidden glow-box mb-12">
          <img src={globalNetwork} alt="Global partner network" className="w-full h-[450px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { region: "Europe", partners: "12,400+", flag: "🇪🇺" },
                { region: "Asia Pacific", partners: "8,200+", flag: "🌏" },
                { region: "Middle East", partners: "5,100+", flag: "🌍" },
                { region: "Americas", partners: "6,300+", flag: "🌎" },
              ].map((r, i) => (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="p-3 rounded-lg glass-card text-center">
                  <div className="text-lg mb-1">{r.flag}</div>
                  <div className="font-display text-xs font-semibold">{r.region}</div>
                  <div className="text-primary text-xs font-bold">{r.partners}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </Section>

    {/* Success Stories */}
    <Section>
      <SectionHeading eyebrow="Partner Stories" title="Voices from Our Community" description="Real experiences from real partners participating in AeroVolt energy projects." />
      <div className="grid md:grid-cols-3 gap-6">
        {[
          { name: "Sarah Mitchell", role: "Energy Investor", location: "London, UK", quote: "What sets AeroVolt apart is the tangibility. I've visited the Highland Ridge wind farm personally — seeing those turbines generating electricity for the grid gives me genuine confidence in what I'm participating in.", since: "Partner since 2021" },
          { name: "Ahmed Al-Rashid", role: "Business Owner", location: "Dubai, UAE", quote: "The weekly distributions have been remarkably consistent. The dashboard gives me complete visibility into every asset's performance. I've now introduced 14 members of my business network to the platform.", since: "Partner since 2022" },
          { name: "Dr. Elena Torres", role: "Environmental Scientist", location: "Madrid, Spain", quote: "As an environmental scientist, I scrutinized AeroVolt's operations before participating. The infrastructure is real, the PPAs are verifiable, and the environmental impact is genuinely positive. This is how renewable energy should scale.", since: "Partner since 2023" },
        ].map((t, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.15 }} className="p-6 rounded-2xl glass-card glass-card-hover transition-all duration-500">
            <div className="flex gap-1 mb-4">
              {[...Array(5)].map((_, j) => (<span key={j} className="text-primary text-sm">★</span>))}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6 italic">"{t.quote}"</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full energy-gradient flex items-center justify-center text-primary-foreground font-bold text-sm">
                {t.name.split(" ").map(n => n[0]).join("")}
              </div>
              <div>
                <div className="font-display text-sm font-semibold">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.role} • {t.location}</div>
                <div className="text-[10px] text-primary">{t.since}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Impact */}
    <Section className="bg-secondary/20">
      <SectionHeading eyebrow="Our Impact" title="Making a Difference Together" />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { icon: Zap, value: "2.4 TWh", label: "Clean Energy Generated", desc: "Enough to power 720,000 homes for a full year" },
          { icon: Heart, value: "1.2M", label: "Tonnes CO₂ Avoided", desc: "Equivalent to removing 260,000 cars from the road" },
          { icon: Users, value: "32,000+", label: "Lives Impacted", desc: "Partners earning from renewable energy participation" },
          { icon: Award, value: "3", label: "Industry Awards", desc: "Recognized for excellence in clean energy participation" },
        ].map((item, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="text-center p-6 rounded-2xl glass-card glass-card-hover transition-all duration-500 group">
            <div className="w-14 h-14 rounded-2xl energy-gradient flex items-center justify-center mx-auto mb-4 group-hover:shadow-[0_0_20px_hsl(155_80%_50%/0.3)] transition-shadow">
              <item.icon className="w-7 h-7 text-primary-foreground" />
            </div>
            <div className="font-display text-2xl font-bold text-primary glow-text mb-1">{item.value}</div>
            <div className="font-display text-sm font-semibold mb-2">{item.label}</div>
            <div className="text-xs text-muted-foreground">{item.desc}</div>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* CTA */}
    <Section>
      <div className="text-center max-w-2xl mx-auto">
        <Globe className="w-12 h-12 text-primary mx-auto mb-6 animate-pulse-glow" />
        <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Growing Community</h2>
        <p className="text-muted-foreground mb-8">Become part of a global network driving the transition to sustainable energy.</p>
        <Link to="/packages" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all glow-box hover:glow-box-hover text-sm">
          View Participation Plans <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Section>
  </div>
);

export default Community;
